import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import SectionTitle from "../../components/SectionTitle";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;

/* ── Hospital icons per keyword ─────────────────────── */
const ICON_MAP = [
  { keys: ["cardio", "heart"], icon: "fa-solid fa-heart-pulse" },
  { keys: ["dental", "tooth"], icon: "fa-solid fa-tooth" },
  { keys: ["neurol", "brain"], icon: "fa-solid fa-brain" },
  { keys: ["ortho", "bone", "joint"], icon: "fa-solid fa-bone" },
  { keys: ["paediat", "pediatr", "child"], icon: "fa-solid fa-baby" },
  { keys: ["gynae", "gynec", "obstet", "maternity"], icon: "fa-solid fa-venus" },
  { keys: ["eye", "ophthal"], icon: "fa-solid fa-eye" },
  { keys: ["uro", "kidney"], icon: "fa-solid fa-kidneys" },
  { keys: ["pulmon", "lung", "respirat"], icon: "fa-solid fa-lungs" },
  { keys: ["surge", "operat"], icon: "fa-solid fa-scalpel" },
  { keys: ["oncol", "cancer"], icon: "fa-solid fa-ribbon" },
  { keys: ["haemat", "hemat", "blood"], icon: "fa-solid fa-droplet" },
  { keys: ["internal", "general"], icon: "fa-solid fa-stethoscope" },
  { keys: ["emerg", "icu", "critical"], icon: "fa-solid fa-truck-medical" },
  { keys: ["radio", "imaging", "mri", "xray"], icon: "fa-solid fa-x-ray" },
  { keys: ["lab", "pathol"], icon: "fa-solid fa-flask-vial" },
  { keys: ["skin", "dermat"], icon: "fa-solid fa-hand-dots" },
  { keys: ["audio", "ear", "ent"], icon: "fa-solid fa-ear-listen" },
  { keys: ["psych", "mental"], icon: "fa-solid fa-head-side-brain" },
  { keys: ["physio", "rehab"], icon: "fa-solid fa-person-walking-with-cane" },
  { keys: ["diet", "nutrit"], icon: "fa-solid fa-apple-whole" },
];

function getServiceIcon(name) {
  const lower = (name || "").toLowerCase();
  for (const { keys, icon } of ICON_MAP) {
    if (keys.some((k) => lower.includes(k))) return icon;
  }
  return "fa-solid fa-hospital-user";
}

/* ── Card ───────────────────────────────────────────── */
function ServiceCard({ item }) {
  const [imgSrc, setImgSrc] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (!item.hasImage) return;
    let cancelled = false;
    fetch(`${BASE}/website/service/${item.id}/image`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!cancelled && blob) {
          urlRef.current = URL.createObjectURL(blob);
          setImgSrc(urlRef.current);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [item.id, item.hasImage]);

  const icon = getServiceIcon(item.name);

  return (
    <div className="mh-svc-card">
      <div className="mh-svc-card__thumb">
        {imgSrc ? (
          <img src={imgSrc} alt={item.name} className="mh-svc-card__img" />
        ) : (
          <div className="mh-svc-card__icon-wrap">
            <i className={icon} />
          </div>
        )}
        <div className="mh-svc-card__badge">
          <i className={icon} />
        </div>
      </div>
      <div className="mh-svc-card__body">
        <h4 className="mh-svc-card__title">{item.name}</h4>
        {item.description && <p className="mh-svc-card__desc">{item.description}</p>}
        <Link to={`/service/${item.id}`} className="mh-svc-card__btn">
          View Details <i className="fa-solid fa-arrow-right" />
        </Link>
      </div>
    </div>
  );
}

/* ── Skeleton ───────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="mh-svc-card mh-svc-card--skeleton">
      <div className="mh-svc-sk-thumb" />
      <div className="mh-svc-card__body">
        <div className="mh-svc-sk-line mh-svc-sk-line--title" />
        <div className="mh-svc-sk-line" />
        <div className="mh-svc-sk-line mh-svc-sk-line--short" />
      </div>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────── */
const ServiceMain = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    makeRequest({ route: "/website/service/public", method: "get" })
      .then(({ data }) => {
        if (Array.isArray(data)) setServices(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim() ? services.filter((s) => s.name.toLowerCase().includes(search.toLowerCase())) : services;

  return (
    <main>
      <Breadcrumb title="Services" />

      <div className="mh-svc-section pt-120 pb-90">
        <div className="container">
          {/* Title + search */}
          <div className="mh-svc-header mb-60">
            <SectionTitle
              itemClass="it-section-title-box text-center"
              subTitleClass="it-section-subtitle"
              subTitle="WHAT WE OFFER"
              titleClass="it-section-title"
              title="Our Medical Services"
            />
            <p className="mh-svc-header__sub">World-class care delivered by expert clinicians across every specialty.</p>
            <div className="mh-svc-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input type="text" placeholder="Search a service…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {/* Grid */}
          <div className="mh-svc-grid">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : filtered.length > 0 ? (
              filtered.map((item) => <ServiceCard key={item.id} item={item} />)
            ) : (
              <div className="mh-svc-empty">
                <i className="fa-solid fa-stethoscope" />
                <p>No services found{search ? ` for "${search}"` : ""}.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ServiceMain;
